import { CircularProgress, Grid, Typography } from "@mui/material"
import {
  FC,
  PropsWithChildren,
  createContext,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { withScorm } from "react-scorm-provider"
import { FullContainer } from "../../../components/FullContainer"
import dialogJson from "../../../data/dialogs.json"
import { DialogKey } from "../../dialogs/Dialogs"
import { useScoreAPI } from "../../score/context/ScoreProvider"
import { SCO, ScormAPI } from "../ScormAPI"

//  CONTEXTS
const ScormAPIContext = createContext<ScormAPI>({} as ScormAPI)

const ScormAPIWrap: FC<PropsWithChildren<{ sco: SCO }>> = ({
  children,
  sco,
}) => {
  console.log("scorm api provider render")

  //  OTHER CONTEXTS
  const { completePOI } = useScoreAPI()

  //  LOGIC
  const [isScormInit, setIsScormInit] = useState(false)

  useEffect(() => {
    if (sco.apiConnected && !isScormInit) {
      //  check existing user scorm objectives
      const objectivesCount = parseInt(sco.get("cmi.objectives._count"))

      for (let i = 1; i < objectivesCount; i++) {
        const id = sco.get(`cmi.objectives.${i}.id`)
        const raw = parseInt(sco.get(`cmi.objectives.${i}.score.raw`)) ?? 0
        if (raw === 1) completePOI(id)
      }

      //  set missing objectives data (first connexion)
      if (objectivesCount <= 1 || !objectivesCount) {
        if (objectivesCount === 0) {
          sco.set(`cmi.objectives.0.id`, "PRIMARYOBJ")
        }
        Object.keys(dialogJson).map((dialogKey, i) => {
          sco.set(`cmi.objectives.${i + 1}.id`, dialogKey)
          sco.set(`cmi.objectives.${i + 1}.completion_status`, "unknown")
          sco.set(`cmi.objectives.${i + 1}.success_status`, "unknown")
          sco.set(`cmi.objectives.${i + 1}.score.min`, 0)
          sco.set(`cmi.objectives.${i + 1}.score.max`, 1)
          sco.set(`cmi.objectives.${i + 1}.score.raw`, 0)
          sco.set(`cmi.objectives.${i + 1}.score.progress_measure`, 0)
        })
      }

      startTransition(() => setIsScormInit(true))
    } else if (
      window.location.origin === "http://localhost:3000" ||
      process.env.REACT_APP_DEV_MODE
    ) {
      startTransition(() => setIsScormInit(true))
    }
  }, [sco.apiConnected, completePOI, isScormInit])

  //  DATA REDUCER
  //  useless at the moment
  // const { data, dispatch } = useScormAPIReducer({ sco });

  //  API
  const api = useMemo<ScormAPI>(() => {
    const completeScormObjective = (id: DialogKey) => {
      console.log("completeScormObjective", sco.apiConnected)
      if (sco.apiConnected) {
        //  find objective index
        const objectivesCount = parseInt(sco.get("cmi.objectives._count"))
        let objectiveIndex = -1
        for (let i = 1; i < objectivesCount; i++) {
          if (sco.get(`cmi.objectives.${i}.id`) === id) {
            objectiveIndex = i
            break
          }
        }

        console.log("validate scorm objective", objectiveIndex)

        //  validate objective
        if (objectiveIndex !== -1) {
          sco.set(`cmi.objectives.${objectiveIndex}.score.raw`, 1)
          sco.set(`cmi.objectives.${objectiveIndex}.score.scaled`, 1)
          sco.set(
            `cmi.objectives.${objectiveIndex}.score.success_status`,
            "passed"
          )
          sco.set(
            `cmi.objectives.${objectiveIndex}.score.completion_status`,
            "completed"
          )
        }
      }
    }

    const completeSCO = () => {
      if (sco.apiConnected) {
        sco.set(`cmi.objectives.0.score.raw`, 1)
        sco.set(`cmi.objectives.0.score.scaled`, 1)
        sco.set(`cmi.objectives.0.score.success_status`, "passed")
        sco.set(`cmi.objectives.0.score.completion_status`, "completed")
        sco.setScore({ value: 100, min: 0, max: 100, status: "completed" })
      }
    }

    return { completeScormObjective, completeSCO }
  }, [sco.apiConnected])

  //  RENDER
  const ChildrenRender = useMemo(() => {
    return isScormInit ? (
      children
    ) : (
      <FullContainer>
        <Grid
          container
          height="100vh"
          flexDirection="column"
          sx={{ backgroundColor: "lightBlu.main" }}
        >
          <CircularProgress />
          <Typography>Loading SCORM</Typography>
        </Grid>
      </FullContainer>
    )
  }, [isScormInit])

  return (
    <ScormAPIContext.Provider value={api}>
      {ChildrenRender}
    </ScormAPIContext.Provider>
  )
}

export const UseScormAPI = () => useContext(ScormAPIContext)

//  SETUP THE HOC FOR GET SCO PROPS INSIDE ScormAPIWrap
export const ScormAPIProvider = withScorm()(ScormAPIWrap)
